import { useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { customerMsg, t } from '../../../../common/i18n';
import { formatAddress } from '../../../../common/utils/accountUtils';
import { DeliveryPlace, emptyAddressDefaults } from '../../placeOrderTypes';
import { AddressSelectionOptions, createEditableAddressOptions } from './AddressOption';
import { DeliveryStepAddress } from './DeliveryStepAddress';
import type { CompanyInfoResponse } from '../../../../generated/api/companyInfoResponse';
import type { DeliveryAddress } from '../../../../generated/api/deliveryAddress';
import type { AuthenticatedUser } from '../../../../public/site/authState';
import type { PlaceOrderFormData } from '../../placeOrderTypes';

import './AddressSelection.scss';

export interface AddressSelectionProps {
  company: CompanyInfoResponse;
  user: AuthenticatedUser;
  onClearError?: () => void;
}

export const mapCompanyToDeliveryAddress = (
  company: CompanyInfoResponse,
  user: AuthenticatedUser
): DeliveryAddress | undefined => {
  if (!company.address) {
    return undefined;
  }

  return {
    companyName: company.companyName ?? '',
    recipient: `${user.firstName} ${user.lastName}`,
    phoneNumber: user.mobile ?? company.phoneNumber ?? '',
    address: company.address,
  };
};

export const AddressSelection = ({ company, user, onClearError }: AddressSelectionProps) => {
  const { setValue, watch } = useFormContext<PlaceOrderFormData>();

  const formDeliveryPlace = watch('delivery.deliveryPlace');
  const formDeliveryAddress = watch('delivery.deliveryAddress');

  const [editingAddress, setEditingAddress] = useState(false);

  const otherAddressMethods = useForm<DeliveryAddress>({ defaultValues: emptyAddressDefaults(company.companyName) });
  const homeAddressMethods = useForm<DeliveryAddress>({ defaultValues: emptyAddressDefaults(company.companyName) });

  const handleRadioChange = (place: DeliveryPlace) => {
    setValue('delivery.deliveryPlace', place);
    onClearError?.();

    if (place === DeliveryPlace.COMPANY_ADDRESS) {
      const companyAddress = mapCompanyToDeliveryAddress(company, user);
      if (companyAddress) {
        setValue('delivery.deliveryAddress', companyAddress);
      }
      setEditingAddress(false);
    } else if (!formDeliveryAddress || place !== formDeliveryPlace) {
      setValue('delivery.deliveryAddress', undefined);
      setEditingAddress(true);
    }
  };

  const handleSetAddress = (address: DeliveryAddress) => {
    setValue('delivery.deliveryAddress', address);
    setEditingAddress(false);
  };

  const renderAddressEditor = (methods: typeof homeAddressMethods) => (
    <FormProvider {...methods}>
      <DeliveryStepAddress
        companyName={company.companyName}
        onSetAddress={handleSetAddress}
        onCancel={() => setEditingAddress(false)}
      />
    </FormProvider>
  );

  const addressOptions = createEditableAddressOptions({
    deliveryAddress: formDeliveryAddress,
    deliveryPlace: formDeliveryPlace,
    editingAddress,
    homeEditor: renderAddressEditor(homeAddressMethods),
    homeMethods: homeAddressMethods,
    onSelect: handleRadioChange,
    otherEditor: renderAddressEditor(otherAddressMethods),
    otherMethods: otherAddressMethods,
    setEditingAddress,
  });

  return (
    <div className="of-place-order__address-selection">
      <p>{t.AK55('Select delivery address')}</p>

      <AddressSelectionOptions
        companySummary={`${formatAddress(company.address)}, ${company.companyName} | ${t.IY5V(customerMsg)}: ${user.firstName} ${user.lastName}, ${t.VG7P('Tel. ')}${user.mobile}`}
        home={addressOptions.home}
        onSelect={handleRadioChange}
        other={addressOptions.other}
        selectedPlace={formDeliveryPlace}
      />
    </div>
  );
};
